import { useQueryParams } from "@certn/api";
import {
  DateRangePicker,
  AvatarTrigger,
  PrimaryButton,
} from "@certn/cascadia-react";
import { _ResponsivePopover } from "@certn/cascadia-react/components/_assets/_ResponsivePopover";
import { parseDateFromValue } from "@certn/cascadia-react/components/inputs/date-input/utils";
import { PickerItem } from "@certn/cascadia-react/components/picker/types";
import { CascadiaMiniIcon } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { CalendarDate } from "@internationalized/date";
import { t } from "i18next";
import { RefObject, useEffect, useState } from "react";
import {
  DateValue,
  DialogTrigger,
  ListBoxProps,
  PopoverProps,
  SelectionMode,
} from "react-aria-components";

export interface DateRangeFilterProps<T>
  extends Omit<ListBoxProps<T>, "children"> {
  isLoading?: boolean;
  label: string;
  showActionBar?: boolean;
  testID?: string;
  clearable?: boolean;
  trigger?: (props: TriggerProps<T>) => React.ReactNode;
  placement?: PopoverProps["placement"];
  minWidth?: string;
  maxWidth?: string;
  actionText?: string;
  isDisabled?: boolean;
  hideClear?: boolean;
  triggerRef?: RefObject<HTMLDivElement>;
  name?: string;
}

interface RangeValue {
  start: DateValue;
  end: DateValue;
}

export function DateRangeFilter<T extends PickerItem>(
  props: DateRangeFilterProps<T>
) {
  const {
    label,
    testID = "date-range-filter-trigger",
    trigger = AvatarTrigger,
    placement = "bottom left",
    minWidth = "280px",
    maxWidth = "320px",
    triggerRef,
    name = "date_range",
  } = props;

  const [isOpen, setOpen] = useState(false);
  const [filterLabel, setFilterLabel] = useState<string>(props.label);

  const items = props.items ? Array.from(props.items) : [];
  const paramKey = "date_range";
  const { flatParams, setParam, getParamsAsArray } = useQueryParams();

  const selectedItems = items.filter((item) => {
    return (
      props.selectedKeys && Array.from(props.selectedKeys).includes(item.id)
    );
  });

  useEffect(() => {
    if (!flatParams[paramKey]) {
      setFilterLabel(label);
    } else {
      const [start, end] = flatParams[paramKey].split(",");
      formatDateAndSetLabel({ start, end });
    }
  }, [label, flatParams]);

  const formatDateAndSetLabel = (dateRange: { start: string; end: string }) => {
    const start = {
      year: dateRange.start.split("-")[0],
      month: dateRange.start.split("-")[1],
      day: dateRange.start.split("-")[2],
    };
    const end = {
      year: dateRange.end.split("-")[0],
      month: dateRange.end.split("-")[1],
      day: dateRange.end.split("-")[2],
    };
    const formattedDateRange = `${start.month}/${start.day}/${start.year} - ${end.month}/${end.day}/${end.year}`;
    setFilterLabel(formattedDateRange);
  };

  const getRangeValueFromParam = (): {
    start: CalendarDate;
    end: CalendarDate;
  } | null => {
    if (getParamsAsArray(paramKey).length === 0) {
      return null;
    }
    const dateRangeString = getParamsAsArray(paramKey)[0].split(" ")[0];
    const dateRange = {
      start: dateRangeString.split(",")[0],
      end: dateRangeString.split(",")[1],
    };

    const parsedValue = parseDateFromValue(dateRange);
    return parsedValue && "start" in parsedValue
      ? {
          start: new CalendarDate(
            parsedValue.start.year,
            parsedValue.start.month,
            parsedValue.start.day
          ),
          end: new CalendarDate(
            parsedValue.end.year,
            parsedValue.end.month,
            parsedValue.end.day
          ),
        }
      : null;
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setOpen}>
      {trigger({
        label: filterLabel,
        isOpen,
        onToggle: () => setOpen((curr) => !curr),
        count: selectedItems.length,
        selectedItems,
        selectionMode: props.selectionMode,
        icon: "calendar_month",
        testID: `${testID}-trigger`,
      })}
      <_ResponsivePopover
        isDismissable
        isOpen={isOpen}
        onOpenChange={setOpen}
        popoverPlacement={placement}
        popoverMinWidth={triggerRef?.current?.clientWidth || minWidth}
        popoverMaxWidth={maxWidth}
      >
        <DateRangePicker
          label={filterLabel}
          testID={testID}
          startName={`${name}-start`}
          endName={`${name}-end`}
          isStatic
          aria-label={t("Date range picker")}
          value={getRangeValueFromParam()}
          onChange={(value: RangeValue | null) => {
            const dateRange = {
              start: value?.start?.toString(),
              end: value?.end?.toString(),
            };
            if (dateRange.start && dateRange.end) {
              formatDateAndSetLabel({
                start: dateRange.start,
                end: dateRange.end,
              });
              setParam(paramKey, `${dateRange.start},${dateRange.end}`, true);
              setOpen(false);
            }
          }}
        />
        <div
          className={css({
            display: "flex",
            justifyContent: "flex-end",
            padding: "var(--space-3) var(--space-4)",
          })}
        >
          <PrimaryButton
            testID="clear"
            onPress={() => {
              setParam(paramKey, "");
              setOpen(false);
            }}
          >
            {t("VERB Clear", "Clear")}
          </PrimaryButton>
        </div>
      </_ResponsivePopover>
    </DialogTrigger>
  );
}

interface TriggerProps<T> {
  count?: number;
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  selectedItems: T[];
  selectionMode: SelectionMode | undefined;
  icon?: CascadiaMiniIcon;
  testID: string;
}
